/**
 * IdentityStore — Atomic JSON persistence for the device identity file.
 *
 * Stores the `PersistedDeviceIdentity` (incl. the HMAC secret cleartext)
 * at `~/.serviceme/device.json` (per `phase-5-device-header-spec.md`
 * §3.1). Writes are atomic via `write-tmp + fsync + rename`, matching
 * the `SkillStore` / `ToolboxStore` precedent. Concurrent writes are
 * serialized with a mkdir-based file lock (POSIX-atomic) — proper
 * cross-process locking is deferred to Phase 6+ per the open spec.
 *
 * The file mode is `0600` (owner read/write only) so the cleartext
 * secret stays safe at rest. On Windows the mode hint is a no-op
 * (Windows uses ACLs) but `writeFile` still succeeds.
 *
 * Migration — IdentityStore auto-detects a v0-shape (pre-Phase-5.2)
 * file written by the Extension's old `globalState` blob:
 *   { version: 1, claimed: false, publicKeyFingerprint: null }
 * In that case the file is migrated forward to the v1 schema on the
 * next write (the data fields are empty and a fresh enroll is required).
 * The full Extension `globalState` → JSON migration happens in the
 * Phase 5.5 adapter (`apps/extension/.../DeviceService.ts`) since the
 * adapter holds the live `globalState` access.
 *
 * Refs:
 * - 4.功能规划.md §2.2 — `IdentityStore.ts 持久化到 ~/.config/serviceme/device.json, 原子写`
 * - `docs/architecture/phase-5-device-header-spec.md` §3.1, §2.5
 */

import * as fsp from "node:fs/promises";
import * as os from "node:os";
import * as path from "node:path";
import { setTimeout as delay } from "node:timers/promises";

import { getDeviceJsonPath, getServicemeHome } from "../paths/userHome";

import {
	type AtomicWriteResult,
	DEVICE_JSON_SCHEMA_VERSION,
	type IdentityStoreHooks,
	type PersistedDeviceIdentity,
} from "./types";

const FILE_MODE = 0o600;
const LOCK_DIR_MODE = 0o700;
const DEFAULT_LOCK_TIMEOUT_MS = 5000;
const DEFAULT_LOCK_RETRY_MS = 25;
const TMP_SUFFIX = ".tmp";

/**
 * Minimal interface for reading + writing the persisted identity file.
 * Default impl uses `getDeviceJsonPath()` (which honors `SERVICEME_HOME`),
 * but tests can substitute a custom path for isolation.
 */
export interface IdentityFileBackend {
	read(filePath: string): Promise<PersistedDeviceIdentity | null>;
	write(filePath: string, payload: PersistedDeviceIdentity): Promise<AtomicWriteResult>;
	exists(filePath: string): Promise<boolean>;
	delete(filePath: string): Promise<void>;
	listDir?(dir: string): Promise<string[]>;
}

export interface IdentityStoreOptions {
	filePath?: string;
	hooks?: IdentityStoreHooks;
	lockTimeoutMs?: number;
	lockRetryMs?: number;
	/** Injectable clock for deterministic tests. */
	now?: () => Date;
	backend?: IdentityFileBackend;
}

/**
 * Default file backend — uses `node:fs/promises` with the canonical
 * tmp-then-rename atomic-write pattern.
 */
export class FsIdentityFileBackend implements IdentityFileBackend {
	async exists(filePath: string): Promise<boolean> {
		try {
			await fsp.access(filePath);
			return true;
		} catch {
			return false;
		}
	}

	async read(filePath: string): Promise<PersistedDeviceIdentity | null> {
		try {
			const buf = await fsp.readFile(filePath, "utf8");
			const parsed = JSON.parse(buf) as unknown;
			return migratePersistedIdentity(parsed);
		} catch (err) {
			if (isNodeError(err) && err.code === "ENOENT") return null;
			throw err;
		}
	}

	async write(filePath: string, payload: PersistedDeviceIdentity): Promise<AtomicWriteResult> {
		await fsp.mkdir(path.dirname(filePath), { recursive: true });
		const tmpPath = `${filePath}${TMP_SUFFIX}`;
		const bytes = Buffer.from(JSON.stringify(payload, null, "\t"), "utf8");
		// Ensure tmp is fresh (in case a previous run died mid-write).
		await fsp.rm(tmpPath, { force: true });
		const handle = await fsp.open(tmpPath, "w", FILE_MODE);
		try {
			await handle.writeFile(bytes);
			await handle.sync();
		} finally {
			await handle.close();
		}
		await fsp.rename(tmpPath, filePath);
		// Best-effort chmod for filesystems that ignore mode on create (Windows).
		await fsp.chmod(filePath, FILE_MODE).catch(() => undefined);
		return { bytesWritten: bytes.byteLength, tmpPath };
	}

	async delete(filePath: string): Promise<void> {
		await fsp.rm(filePath, { force: true });
	}
}

/**
 * Reconcile an unknown on-disk shape into the current `PersistedDeviceIdentity`.
 *
 * - v1 IdentityStore files (current shape) pass through unchanged.
 * - v0 bootstrap files (`{ version: 1, claimed: false, publicKeyFingerprint: null }`)
 *   are recognized by their placeholder keys and discarded; the next
 *   enroll writes a fresh identity.
 * - Anything else throws — refuse to silently drop user data.
 */
function migratePersistedIdentity(parsed: unknown): PersistedDeviceIdentity | null {
	if (!isRecord(parsed)) {
		throw new Error("device.json: top-level must be an object");
	}
	const version = parsed.version;
	if (version === DEVICE_JSON_SCHEMA_VERSION) {
		// Pre-Phase-5.2 placeholder shape carries `claimed` /
		// `publicKeyFingerprint` but no real device fields. Recognize
		// the marker and return null so the next enroll writes fresh data.
		if (
			parsed.publicId === undefined &&
			parsed.deviceSecret === undefined &&
			("claimed" in parsed || "publicKeyFingerprint" in parsed)
		) {
			return null;
		}
		// Trust the schema — the writer is also us.
		return parsed as unknown as PersistedDeviceIdentity;
	}
	if (typeof version === "number" && version < DEVICE_JSON_SCHEMA_VERSION) {
		// Pre-Phase-5.2 bootstrap shape — the file is empty placeholder
		// data; nothing to migrate. Return null to signal "no identity".
		return null;
	}
	throw new Error(`device.json: unsupported schema version ${String(version)}`);
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function isNodeError(value: unknown): value is NodeJS.ErrnoException {
	return value instanceof Error && typeof (value as { code?: unknown }).code === "string";
}

const LOCK_PID_FILE = "pid";

/**
 * Check whether a process is still alive (best-effort, cross-platform).
 * Returns `false` for any PID we cannot verify as alive.
 */
function isProcessAlive(pid: number): boolean {
	try {
		// signal 0 — permission check only, never actually sent
		process.kill(pid, 0);
		return true;
	} catch {
		return false;
	}
}

/**
 * mkdir-based advisory file lock with stale-lock recovery.
 *
 * POSIX mkdir is atomic; on Windows modern filesystems (NTFS) it's also
 * atomic at the API level. Sufficient for single-host, single-user
 * scenarios (which is the SERVICEME threat model).
 *
 * Stale lock recovery: a `pid` file inside the lock directory records the
 * owner's PID. On `EEXIST`, if the recorded PID is no longer alive, the
 * lock directory is forcibly removed and acquisition retried immediately.
 * This prevents permanent lockout when a process crashes without calling
 * `release()`.
 */
class FileLock {
	private readonly dirPath: string;
	private readonly pidFilePath: string;
	private readonly timeoutMs: number;
	private readonly retryMs: number;
	private acquired = false;

	constructor(filePath: string, timeoutMs: number, retryMs: number) {
		this.dirPath = `${filePath}.lock`;
		this.pidFilePath = path.join(this.dirPath, LOCK_PID_FILE);
		this.timeoutMs = timeoutMs;
		this.retryMs = retryMs;
	}

	async acquire(): Promise<void> {
		const start = Date.now();
		while (true) {
			try {
				await fsp.mkdir(this.dirPath, { mode: LOCK_DIR_MODE });
				// Write PID so a future acquirer can detect if we crash.
				await fsp.writeFile(this.pidFilePath, String(process.pid), "utf8").catch(() => undefined);
				this.acquired = true;
				return;
			} catch (err) {
				if (!isNodeError(err) || err.code !== "EEXIST") {
					throw err;
				}
				// Lock directory exists — check for stale owner.
				const stale = await this.isStaleLock();
				if (stale) {
					await fsp.rm(this.dirPath, { recursive: true, force: true });
					// Retry immediately without counting this iteration against timeout.
					continue;
				}
				if (Date.now() - start >= this.timeoutMs) {
					throw new Error(`IdentityStore lock acquisition timed out for ${this.dirPath}`);
				}
				await delay(this.retryMs);
			}
		}
	}

	private async isStaleLock(): Promise<boolean> {
		try {
			const pidStr = await fsp.readFile(this.pidFilePath, "utf8");
			const pid = Number.parseInt(pidStr.trim(), 10);
			if (!Number.isFinite(pid) || pid <= 0) return true; // malformed pid file → treat as stale
			return !isProcessAlive(pid);
		} catch {
			// pid file unreadable / missing — assume stale
			return true;
		}
	}

	async release(): Promise<void> {
		if (!this.acquired) return;
		this.acquired = false;
		await fsp.rm(this.dirPath, { recursive: true, force: true });
	}
}

export class IdentityStore {
	private readonly filePath: string;
	private readonly backend: IdentityFileBackend;
	private readonly hooks: IdentityStoreHooks;
	private readonly lockTimeoutMs: number;
	private readonly lockRetryMs: number;
	private readonly now: () => Date;

	constructor(opts: IdentityStoreOptions = {}) {
		this.filePath = opts.filePath ?? getDeviceJsonPath();
		this.backend = opts.backend ?? new FsIdentityFileBackend();
		this.hooks = opts.hooks ?? {};
		this.lockTimeoutMs = opts.lockTimeoutMs ?? DEFAULT_LOCK_TIMEOUT_MS;
		this.lockRetryMs = opts.lockRetryMs ?? DEFAULT_LOCK_RETRY_MS;
		this.now = opts.now ?? (() => new Date());
	}

	/** Absolute path to the underlying JSON file (test seam). */
	getFilePath(): string {
		return this.filePath;
	}

	/** True when the JSON file already exists on disk. */
	async exists(): Promise<boolean> {
		return this.backend.exists(this.filePath);
	}

	/** Read the persisted identity; returns `null` when no identity is stored. */
	async read(): Promise<PersistedDeviceIdentity | null> {
		return this.backend.read(this.filePath);
	}

	/**
	 * Atomically write the given identity. Concurrent writers are
	 * serialized via the file lock; the read-modify-write happens
	 * inside the lock so callers can't see a partial state.
	 */
	async write(next: PersistedDeviceIdentity): Promise<AtomicWriteResult> {
		await this.hooks.beforeWrite?.(next);
		const lock = new FileLock(this.filePath, this.lockTimeoutMs, this.lockRetryMs);
		await lock.acquire();
		try {
			const stamped: PersistedDeviceIdentity = {
				...next,
				version: DEVICE_JSON_SCHEMA_VERSION,
			};
			const result = await this.backend.write(this.filePath, stamped);
			await this.hooks.afterWrite?.(stamped);
			return result;
		} finally {
			await lock.release();
		}
	}

	/**
	 * Read-modify-write under the same lock. The mutator receives the
	 * current identity (or `null` on first call) and returns the
	 * replacement. Throwing inside the mutator aborts the write.
	 */
	async mutate<T>(
		mutator: (
			current: PersistedDeviceIdentity | null
		) => Promise<{ next: PersistedDeviceIdentity; result?: T }>
	): Promise<{ result: T | undefined; written: PersistedDeviceIdentity }> {
		const lock = new FileLock(this.filePath, this.lockTimeoutMs, this.lockRetryMs);
		await lock.acquire();
		try {
			const current = await this.backend.read(this.filePath);
			const { next, result } = await mutator(current);
			const stamped: PersistedDeviceIdentity = {
				...next,
				version: DEVICE_JSON_SCHEMA_VERSION,
			};
			await this.hooks.beforeWrite?.(stamped);
			await this.backend.write(this.filePath, stamped);
			await this.hooks.afterWrite?.(stamped);
			return { result, written: stamped };
		} finally {
			await lock.release();
		}
	}

	/** Wipe the persisted identity (used by `device.enroll --force`). */
	async clear(): Promise<void> {
		await this.backend.delete(this.filePath);
	}

	/**
	 * Resolve the installation metadata for the current machine.
	 * Pure helper — no I/O, just `os.*` calls.
	 */
	resolveInstallationMetadata(): Pick<
		PersistedDeviceIdentity,
		"installationId" | "machineId" | "platform"
	> {
		const machineId = os.hostname();
		const platform = os.platform();
		// installationId is derived by `InstallationId.ts` — pass the
		// caller's already-computed value via `material` so we don't
		// recompute the SHA twice in a row.
		return {
			installationId: "", // intentionally empty; caller fills via deriveInstallationId()
			machineId,
			platform,
		};
	}

	/**
	 * Ensure the parent directory exists (`~/.serviceme/`). Idempotent.
	 * Useful when the bootstrap phase5 placeholder wasn't run yet.
	 */
	async ensureHome(): Promise<void> {
		await fsp.mkdir(getServicemeHome(), { recursive: true });
		await fsp.mkdir(path.dirname(this.filePath), { recursive: true });
	}
}
